// Feature-scoped error boundary for isolating a crashed subtree (player, tab,
// results column, chart) without taking down the rest of the page. Unlike
// AppErrorBoundary this renders inside Mantine, so the fallback uses Mantine
// components. Chunk-load recovery still uses the shared one-shot reload — this
// boundary must not swallow deploy-skew import failures as a section Alert.
import React from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { datadogRum } from '@datadog/browser-rum';
import { useTranslation } from 'react-i18next';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import {
  holdChunkReloadGuard,
  isChunkLoadError,
  isReloadInitiated,
  onChunkReloadCancelled,
  releaseChunkReloadGuard,
  reloadOnceForChunkError,
  wasChunkReloadCancelled,
} from 'utils/chunkReload';

interface FeatureErrorBoundaryProps {
  /** Datadog `feature` tag, e.g. `transcript_recording_player`. */
  feature: string;
  children: React.ReactNode;
  /** Shown under the title. */
  description?: string;
  /**
   * `section` is the default page-band Alert. `compact` drops outer margin so
   * the fallback fits a metric tile or dashboard card. `silent` renders nothing
   * on failure, for optional chrome such as a popup whose absence is the
   * fallback; the error is still reported.
   */
  variant?: 'section' | 'compact' | 'silent';
  /** Clear the error state when any value changes (search, step, card id). */
  resetKeys?: readonly unknown[];
  /**
   * Extra control beside retry/reload. A container that hides its own dismiss
   * control inside `children` (a drawer whose close button lives in the failed
   * subtree) passes one here so the fallback is still escapable.
   */
  action?: React.ReactNode;
}

// 'reloading' renders nothing while a chunk-error reload is pending.
// 'chunkFailed' is a chunk error with no reload coming (budget spent, or the
// reload was cancelled); React.lazy keeps a rejected payload, so the only
// recovery offered is a page reload.
type FeatureBoundaryStatus = 'ok' | 'reloading' | 'failed' | 'chunkFailed';

interface FeatureErrorBoundaryState {
  status: FeatureBoundaryStatus;
}

function resetKeysEqual(
  prev: readonly unknown[] | undefined,
  next: readonly unknown[] | undefined
): boolean {
  if (prev === next) {
    return true;
  }
  if (prev == null || next == null || prev.length !== next.length) {
    return false;
  }
  return prev.every((value, index) => Object.is(value, next[index]));
}

export class FeatureErrorBoundary extends React.Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  override state: FeatureErrorBoundaryState = { status: 'ok' };
  private holdingChunkGuard = false;
  private unsubscribeCancellation: (() => void) | undefined;
  // The chunk error a pending reload was scheduled for, kept so a StrictMode
  // simulated remount can re-subscribe (componentDidCatch is not replayed).
  private pendingReloadError:
    | { error: Error; errorInfo: React.ErrorInfo }
    | undefined;

  private pinChunkGuard = (): void => {
    if (this.holdingChunkGuard) {
      return;
    }
    this.holdingChunkGuard = true;
    holdChunkReloadGuard();
  };

  private unpinChunkGuard = (): void => {
    if (!this.holdingChunkGuard) {
      return;
    }
    this.holdingChunkGuard = false;
    releaseChunkReloadGuard();
  };

  private stopWatchingCancellation = (): void => {
    this.unsubscribeCancellation?.();
    this.unsubscribeCancellation = undefined;
  };

  private watchReloadCancellation = (
    error: Error,
    errorInfo: React.ErrorInfo
  ): void => {
    if (this.unsubscribeCancellation) {
      return;
    }
    this.pendingReloadError = { error, errorInfo };
    this.unsubscribeCancellation = onChunkReloadCancelled(() => {
      this.pendingReloadError = undefined;
      this.pinChunkGuard();
      this.setState({ status: 'chunkFailed' });
      datadogRum.addError(error, {
        feature: this.props.feature,
        componentStack: errorInfo.componentStack,
        chunkReloadCancelled: true,
      });
    });
  };

  static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
    if (isChunkLoadError(error)) {
      return { status: 'reloading' };
    }
    return { status: 'failed' };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isChunkLoadError(error)) {
      if (
        reloadOnceForChunkError(error, 'feature_error_boundary') ||
        isReloadInitiated()
      ) {
        this.watchReloadCancellation(error, errorInfo);
        return;
      }
      this.pinChunkGuard();
      this.setState({ status: 'chunkFailed' });
      datadogRum.addError(error, {
        feature: this.props.feature,
        componentStack: errorInfo.componentStack,
        chunkReloadCancelled: wasChunkReloadCancelled(),
      });
      return;
    }

    datadogRum.addError(error, {
      feature: this.props.feature,
      componentStack: errorInfo.componentStack,
    });
  }

  override componentDidMount(): void {
    // StrictMode runs componentWillUnmount and componentDidMount again for a
    // fiber that caught during its mounting commit, without replaying
    // componentDidCatch; take the subscription back.
    if (this.state.status === 'reloading' && this.pendingReloadError) {
      this.watchReloadCancellation(
        this.pendingReloadError.error,
        this.pendingReloadError.errorInfo
      );
    }
  }

  override componentDidUpdate(prevProps: FeatureErrorBoundaryProps): void {
    if (
      this.state.status !== 'ok' &&
      !resetKeysEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.stopWatchingCancellation();
      this.unpinChunkGuard();
      this.pendingReloadError = undefined;
      this.setState({ status: 'ok' });
      return;
    }
    // Same StrictMode remount, for a pin taken in componentDidCatch.
    if (this.state.status === 'chunkFailed') {
      this.pinChunkGuard();
    }
  }

  override componentWillUnmount(): void {
    this.stopWatchingCancellation();
    this.unpinChunkGuard();
  }

  handleRetry = (): void => {
    this.stopWatchingCancellation();
    this.unpinChunkGuard();
    this.pendingReloadError = undefined;
    this.setState({ status: 'ok' });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): React.ReactNode {
    if (this.state.status === 'reloading') {
      return null;
    }
    if (this.state.status === 'ok') {
      return this.props.children;
    }
    if (this.props.variant === 'silent') {
      return null;
    }

    return (
      <FeatureErrorFallback
        isCompact={this.props.variant === 'compact'}
        chunkFailed={this.state.status === 'chunkFailed'}
        description={this.props.description}
        action={this.props.action}
        onReload={this.handleReload}
        onRetry={this.handleRetry}
      />
    );
  }
}

function FeatureErrorFallback({
  isCompact,
  chunkFailed,
  description,
  action,
  onReload,
  onRetry,
}: {
  isCompact: boolean;
  chunkFailed: boolean;
  description?: string;
  action?: React.ReactNode;
  onReload: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  return (
    <Alert
      color='red'
      variant='light'
      title={t('errorBoundary.sectionTitle')}
      m={isCompact ? undefined : 'md'}
      p={isCompact ? 'xs' : undefined}
    >
      <Text size='sm' mb='sm'>
        {description ?? t('errorBoundary.sectionDescription')}
      </Text>
      <Group gap='xs'>
        <Button
          size='xs'
          variant='light'
          color='red'
          onClick={chunkFailed ? onReload : onRetry}
        >
          {chunkFailed
            ? t('core:errors.reloadPage')
            : t('core:actions.tryAgain')}
        </Button>
        {action}
      </Group>
    </Alert>
  );
}
